import { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  Activity,
  BarChart3,
  BookOpen,
  CreditCard,
  Gauge,
  LayoutDashboard,
  MessageCircle,
  SearchCode,
  Search,
  Settings,
  ShieldCheck,
  TrendingUp,
  UserPlus,
  Users,
} from "lucide-react";
import {
  countByField,
  getRecentDocuments,
  getTopEntries,
} from "../../services/analyticsService";
import {
  getEnquiriesSince,
  getEnquiriesSummary,
  getWhatsAppDemoLink,
  LEAD_STATUSES,
  updateEnquiryLeadStatus,
} from "../../services/enquiryService";
import { getNewUsersSince, getUsersSummary } from "../../services/userService";
import { routePaths } from "../../constants/routes";
import "../../style/adminDashboard.css";

const emptyDashboardData = {
  totalUsers: 0,
  activeUsers: 0,
  newSignupsThisWeek: 0,
  enquiriesThisWeek: 0,
  totalEnquiries: 0,
  leadTypeCounts: [],
  leadStatusCounts: [],
  topPages: [],
  topSearches: [],
  topCourses: [],
  enquirySources: [],
  dropOffPages: [],
  adminNotifications: [],
  homeToEnrollRate: 0,
  blogToEnquiryRate: 0,
  recentUsers: [],
  recentEnquiries: [],
  leadInbox: [],
};

function getStartOfWeek() {
  const currentDate = new Date();
  const day = currentDate.getDay();
  const diff = currentDate.getDate() - day + (day === 0 ? -6 : 1);
  const startOfWeek = new Date(currentDate.setDate(diff));
  startOfWeek.setHours(0, 0, 0, 0);
  return startOfWeek;
}

function calculateRate(numerator, denominator) {
  if (!denominator) {
    return 0;
  }

  return Math.round((numerator / denominator) * 100);
}

function normalizeSourceLabel(enquiry) {
  if (enquiry.source) {
    return enquiry.source;
  }

  if (enquiry.sourcePath?.includes("blog")) {
    return "blog";
  }

  if (enquiry.sourcePath?.includes("enroll")) {
    return "enroll-page";
  }

  return "direct";
}

function normalizeLeadType(enquiry) {
  if (enquiry.enquiryType) return enquiry.enquiryType;
  if (enquiry.source?.includes("newsletter")) return "newsletter";
  if (enquiry.source?.includes("enroll")) return "demo";
  if (enquiry.courseInterest) return "course";
  return "contact";
}

function normalizeLeadStatus(enquiry) {
  const status = enquiry.leadStatus || enquiry.workflow?.status || "new";
  return status === "new lead" ? "new" : status;
}

function formatFirestoreDate(value) {
  if (!value) return "-";
  const date = typeof value.toDate === "function" ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

async function safeDashboardRead(label, loader, fallbackValue) {
  try {
    return await loader();
  } catch (readError) {
    console.warn(`Admin dashboard optional read failed: ${label}`, readError);
    return fallbackValue;
  }
}

export default function AdminDashboard() {
  const [dashboardData, setDashboardData] = useState(emptyDashboardData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [adminNotice, setAdminNotice] = useState("");
  const [updatingLeadId, setUpdatingLeadId] = useState("");

  useEffect(() => {
    let isMounted = true;

    async function loadDashboardData() {
      setLoading(true);
      setError("");

      try {
        const startOfWeek = getStartOfWeek();
        const [
          usersSummary,
          newSignupsThisWeek,
          enquiriesSummary,
          enquiriesThisWeek,
          pageViews,
          searchLogs,
          ctaClicks,
          courseInterest,
          adminNotifications,
        ] = await Promise.all([
          safeDashboardRead("users", getUsersSummary, { users: [], totalUsers: 0, activeUsers: 0 }),
          safeDashboardRead("new users this week", () => getNewUsersSince(startOfWeek), 0),
          safeDashboardRead("enquiries", getEnquiriesSummary, { enquiries: [], totalEnquiries: 0 }),
          safeDashboardRead("enquiries this week", () => getEnquiriesSince(startOfWeek), 0),
          safeDashboardRead("page_views", () => getRecentDocuments("page_views", 500), []),
          safeDashboardRead("search_logs", () => getRecentDocuments("search_logs", 300), []),
          safeDashboardRead("cta_clicks", () => getRecentDocuments("cta_clicks", 300), []),
          safeDashboardRead("course_interest", () => getRecentDocuments("course_interest", 300), []),
          safeDashboardRead("admin_notifications", () => getRecentDocuments("admin_notifications", 20), []),
        ]);

        const topPages = getTopEntries(countByField(pageViews, "path"), 6);
        const topSearches = getTopEntries(
          countByField(searchLogs, "selectedLabel", "Unselected search"),
          6
        );
        const topCourses = getTopEntries(countByField(courseInterest, "courseTitle"), 6);

        const enquirySourceMap = enquiriesSummary.enquiries.reduce((accumulator, enquiry) => {
          const source = normalizeSourceLabel(enquiry);
          accumulator[source] = (accumulator[source] || 0) + 1;
          return accumulator;
        }, {});

        const leadTypeMap = enquiriesSummary.enquiries.reduce((accumulator, enquiry) => {
          const type = normalizeLeadType(enquiry);
          accumulator[type] = (accumulator[type] || 0) + 1;
          return accumulator;
        }, {});

        const leadStatusMap = enquiriesSummary.enquiries.reduce((accumulator, enquiry) => {
          const status = normalizeLeadStatus(enquiry);
          accumulator[status] = (accumulator[status] || 0) + 1;
          return accumulator;
        }, {});

        const homeViews = pageViews.filter((view) => view.path === "/home").length;
        const homeToEnrollViews = pageViews.filter(
          (view) => view.path === "/enroll" && view.previousPath === "/home"
        ).length;
        const blogViews = pageViews.filter((view) => view.path?.includes("blog")).length;
        const blogEnquiries = enquiriesSummary.enquiries.filter((enquiry) =>
          enquiry.sourcePath?.includes("blog")
        ).length;

        const ctaTargetCounts = countByField(ctaClicks, "sourcePath", "Unknown source");
        const dropOffPages = topPages
          .map((page) => ({
            ...page,
            ctaClicks: ctaTargetCounts[page.label] || 0,
          }))
          .filter((page) => page.value >= 2 && page.ctaClicks === 0)
          .slice(0, 5);

        if (isMounted) {
          setDashboardData({
            totalUsers: usersSummary.totalUsers,
            activeUsers: usersSummary.activeUsers,
            newSignupsThisWeek,
            enquiriesThisWeek,
            totalEnquiries: enquiriesSummary.totalEnquiries,
            leadTypeCounts: getTopEntries(leadTypeMap, 8),
            leadStatusCounts: getTopEntries(leadStatusMap, 8),
            topPages,
            topSearches,
            topCourses,
            enquirySources: getTopEntries(enquirySourceMap, 5),
            dropOffPages,
            adminNotifications,
            homeToEnrollRate: calculateRate(homeToEnrollViews, homeViews),
            blogToEnquiryRate: calculateRate(blogEnquiries, blogViews),
            recentUsers: usersSummary.users.slice(0, 6),
            recentEnquiries: enquiriesSummary.enquiries.slice(0, 6),
            leadInbox: enquiriesSummary.enquiries.slice(0, 25),
          });
        }
      } catch (dashboardError) {
        console.error("Unable to load admin analytics:", dashboardError);
        if (isMounted) {
          setError("Unable to load dashboard data. Please check Firestore rules and admin permissions.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadDashboardData();

    return () => {
      isMounted = false;
    };
  }, []);

  const statCards = useMemo(
    () => [
      {
        label: "Total Users",
        value: dashboardData.totalUsers,
        detail: `${dashboardData.activeUsers} active users`,
        icon: Users,
        tone: "blue",
      },
      {
        label: "New Signups This Week",
        value: dashboardData.newSignupsThisWeek,
        detail: "Fresh user registrations",
        icon: UserPlus,
        tone: "green",
      },
      {
        label: "Enquiries This Week",
        value: dashboardData.enquiriesThisWeek,
        detail: `${dashboardData.totalEnquiries} total enquiries`,
        icon: BookOpen,
        tone: "orange",
      },
      {
        label: "Home to Enroll",
        value: `${dashboardData.homeToEnrollRate}%`,
        detail: "Approx. page transition rate",
        icon: TrendingUp,
        tone: "purple",
      },
      {
        label: "Blog to Enquiry",
        value: `${dashboardData.blogToEnquiryRate}%`,
        detail: "Approx. enquiry conversion",
        icon: Gauge,
        tone: "teal",
      },
    ],
    [dashboardData]
  );

  const handleLeadStatusChange = async (enquiryId, nextStatus) => {
    setUpdatingLeadId(enquiryId);
    setAdminNotice("");

    try {
      await updateEnquiryLeadStatus(enquiryId, nextStatus);

      const updateLeadList = (leads) =>
        leads.map((lead) =>
          lead.id === enquiryId
            ? {
                ...lead,
                leadStatus: nextStatus,
                workflow: {
                  ...(lead.workflow || {}),
                  status: nextStatus,
                },
              }
            : lead
        );

      setDashboardData((currentData) => ({
        ...currentData,
        leadInbox: updateLeadList(currentData.leadInbox),
        recentEnquiries: updateLeadList(currentData.recentEnquiries),
      }));
      setAdminNotice(`Lead status updated to "${nextStatus}".`);
    } catch (statusError) {
      console.error("Unable to update lead status:", statusError);
      setAdminNotice("Unable to update status. Please confirm Firestore rules allow admin enquiry updates.");
    } finally {
      setUpdatingLeadId("");
    }
  };

  return (
    <div className="admin-container">
      <aside className="admin-sidebar">
        <div className="admin-sidebar-brand">
          <strong>QA Coder</strong>
          <span>Admin Control</span>
        </div>

        <nav className="admin-nav-section">
          <p className="admin-nav-label">Navigation</p>
          <ul>
            <li className="active"><LayoutDashboard size={18} /> Dashboard</li>
            <li>
              <NavLink to={routePaths.leadCrm}>
                <MessageCircle size={18} /> Lead CRM
              </NavLink>
            </li>
            <li>
              <NavLink to={routePaths.bugs}>
                <SearchCode size={18} /> Bug Tracker
              </NavLink>
            </li>
            <li><Users size={18} /> User Management</li>
            <li><BookOpen size={18} /> Courses</li>
            <li><BarChart3 size={18} /> Analytics</li>
            <li><CreditCard size={18} /> Payments</li>
          </ul>

          <p className="admin-nav-label">System</p>
          <ul>
            <li><ShieldCheck size={18} /> Access Control</li>
            <li><Settings size={18} /> Settings</li>
          </ul>
        </nav>
      </aside>

      <main className="admin-main-content">
        <section className="admin-content-area">
          <div className="admin-page-title">
            <div>
              <span>Analytics Overview</span>
              <h1>Admin Dashboard</h1>
            </div>
            <div className="admin-live-pill">
              <Activity size={16} />
              Firestore live data
            </div>
          </div>

          {error && <div className="admin-alert">{error}</div>}
          {adminNotice && <div className="admin-info-alert">{adminNotice}</div>}

          {loading ? (
            <div className="admin-loading-card">Loading analytics...</div>
          ) : (
            <>
              <div className="admin-stats-grid">
                {statCards.map((card) => {
                  const Icon = card.icon;

                  return (
                    <article className="admin-stat-card" key={card.label}>
                      <div className={`admin-stat-icon ${card.tone}`}>
                        <Icon size={22} />
                      </div>
                      <div>
                        <h3>{card.value}</h3>
                        <p>{card.label}</p>
                        <span>{card.detail}</span>
                      </div>
                    </article>
                  );
                })}
              </div>

              <div className="admin-analytics-grid">
                <AnalyticsList title="Lead Types" items={dashboardData.leadTypeCounts} />
                <AnalyticsList title="Lead Workflow" items={dashboardData.leadStatusCounts} />
                <AnalyticsList title="Most Visited Pages" items={dashboardData.topPages} />
                <AnalyticsList title="Top Searched Topics" items={dashboardData.topSearches} icon={Search} />
                <AnalyticsList title="Most Opened Course Pages" items={dashboardData.topCourses} />
                <AnalyticsList title="Enquiry Sources" items={dashboardData.enquirySources} />
                <AnalyticsList
                  title="Possible Drop-off Pages"
                  items={dashboardData.dropOffPages.map((page) => ({
                    label: page.label,
                    value: `${page.value} views, ${page.ctaClicks} CTA clicks`,
                  }))}
                />
                <AnalyticsList
                  title="Admin Notifications"
                  items={dashboardData.adminNotifications.slice(0, 5).map((notification) => ({
                    label: notification.title || notification.type,
                    value: notification.read ? "read" : "new",
                  }))}
                />
              </div>

              <div className="admin-table-card admin-lead-inbox-card">
                <div className="admin-table-header">
                  <div>
                    <h3>Complete Lead Inbox</h3>
                    <p>Every newsletter, demo, course, support, and contact request saved in Firestore.</p>
                  </div>
                  <span>{dashboardData.leadInbox.length} latest shown</span>
                </div>
                <div className="admin-responsive-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Lead</th>
                        <th>Type</th>
                        <th>Contact</th>
                        <th>Course / Subject</th>
                        <th>Source</th>
                        <th>Status</th>
                        <th>Auto Action</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData.leadInbox.map((enquiry) => {
                        const leadType = normalizeLeadType(enquiry);
                        const whatsappLink = leadType === "demo"
                          ? getWhatsAppDemoLink({ phone: enquiry.phone, name: enquiry.name })
                          : "";

                        return (
                          <tr key={enquiry.id}>
                            <td>
                              <strong>{enquiry.name || "Website visitor"}</strong>
                              <small>{enquiry.email || "No email"}</small>
                            </td>
                            <td><span className="admin-badge">{leadType}</span></td>
                            <td>{enquiry.phone || enquiry.email || "-"}</td>
                            <td>{enquiry.courseInterest || enquiry.course || enquiry.subject || "-"}</td>
                            <td>{normalizeSourceLabel(enquiry)}</td>
                            <td>
                              <select
                                className="admin-status-select"
                                value={normalizeLeadStatus(enquiry)}
                                onChange={(event) => handleLeadStatusChange(enquiry.id, event.target.value)}
                                disabled={updatingLeadId === enquiry.id}
                              >
                                {LEAD_STATUSES.map((status) => (
                                  <option key={status} value={status}>
                                    {status}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              {whatsappLink ? (
                                <a
                                  className="admin-action-link"
                                  href={whatsappLink}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={() => handleLeadStatusChange(enquiry.id, "auto-response sent")}
                                >
                                  <MessageCircle size={15} />
                                  Send WhatsApp
                                </a>
                              ) : (
                                <span className="admin-muted-text">
                                  {leadType === "demo" ? "Phone needed" : "No action"}
                                </span>
                              )}
                            </td>
                            <td>{formatFirestoreDate(enquiry.createdAt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="admin-table-grid">
                <div className="admin-table-card">
                  <div className="admin-table-header">
                    <h3>Recent Users</h3>
                    <span>{dashboardData.totalUsers} total</span>
                  </div>
                  <div className="admin-responsive-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardData.recentUsers.map((user) => (
                          <tr key={user.id}>
                            <td>{user.fullName || user.displayName || "QA Learner"}</td>
                            <td>{user.email || "-"}</td>
                            <td><span className="admin-badge">{user.role || "student"}</span></td>
                            <td>{user.isActive === false ? "Inactive" : "Active"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="admin-table-card">
                  <div className="admin-table-header">
                    <h3>Recent Enquiries</h3>
                    <span>{dashboardData.totalEnquiries} total</span>
                  </div>
                  <div className="admin-responsive-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Phone</th>
                          <th>Course</th>
                          <th>Type</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardData.recentEnquiries.map((enquiry) => (
                          <tr key={enquiry.id}>
                            <td>{enquiry.name || "-"}</td>
                            <td>{enquiry.phone || "-"}</td>
                            <td>{enquiry.courseInterest || enquiry.course || "-"}</td>
                            <td><span className="admin-badge">{enquiry.enquiryType || normalizeSourceLabel(enquiry)}</span></td>
                            <td><span className="admin-badge">{normalizeLeadStatus(enquiry)}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}
        </section>
      </main>
    </div>
  );
}

function AnalyticsList({ title, items, icon: Icon = BarChart3 }) {
  return (
    <article className="admin-analytics-card">
      <div className="admin-card-title">
        <Icon size={18} />
        <h3>{title}</h3>
      </div>

      {items.length ? (
        <ul>
          {items.map((item) => (
            <li key={item.label}>
              <span>{item.label}</span>
              <strong>{item.value}</strong>
            </li>
          ))}
        </ul>
      ) : (
        <p className="admin-empty-text">No data yet. Events will appear after users interact with the site.</p>
      )}
    </article>
  );
}
